import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    message,
} from "antd";
import dayjs from "dayjs";
import { createTransactionSchedule, getInstitutions } from "../api/transactionScheduleApi";

const currencyOptions = [
    { label: "USD", value: "USD" },
    { label: "CNY", value: "CNY" },
    { label: "RUB", value: "RUB" },
    { label: "MNT", value: "MNT" },
    { label: "EUR", value: "EUR" },
];

function ManualTransactionScheduleForm({ onSaved }) {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [institutions, setInstitutions] = useState([]);
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);

    useEffect(() => {
        const loadInstitutions = async () => {
            try {
                setLoadingInstitutions(true);
                const data = await getInstitutions();
                setInstitutions(data || []);
            } catch (error) {
                message.error(error.message || "Байгууллагууд ачаалж чадсангүй");
            } finally {
                setLoadingInstitutions(false);
            }
        };

        loadInstitutions();
    }, []);

    const handleOpen = () => {
        form.setFieldsValue({
            registrationDateTime: dayjs(),
            requestDateTime: dayjs(),
        });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const selectedInstitution = institutions.find((x) => x.id === values.institutionId);

            const payload = {
                requestNumber: values.requestNumber,
                payer: selectedInstitution ? selectedInstitution.name : "",
                institutionId: values.institutionId || null,
                registrationDateTime: values.registrationDateTime
                    ? values.registrationDateTime.format("YYYY-MM-DDTHH:mm:ss")
                    : null,
                requestDateTime: values.requestDateTime
                    ? values.requestDateTime.format("YYYY-MM-DDTHH:mm:ss")
                    : null,
                currency: values.currency,
                paymentAmount: values.paymentAmount,
                beneficiaryCompany: values.beneficiaryCompany,
                beneficiaryBank: values.beneficiaryBank,
                beneficiaryAccount: values.beneficiaryAccount,
                paymentPurpose: values.paymentPurpose,
            };

            await createTransactionSchedule(payload);
            message.success("Гүйлгээний хуваарь хадгалагдлаа");
            handleClose();
            onSaved?.();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(error.message || "Хадгалж чадсангүй");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button type="primary" onClick={handleOpen}>
                Гараар нэмэх
            </Button>

            <Modal
                open={open}
                title="Гараар нэмэх"
                onCancel={handleClose}
                onOk={handleSubmit}
                confirmLoading={loading}
                okText="Хадгалах"
                cancelText="Болих"
                width={900}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="requestNumber"
                                label="Хүсэлтийн дугаар"
                                rules={[{ required: true, message: "Хүсэлтийн дугаар заавал" }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                name="institutionId"
                                label="Төлөгч / Байгууллага"
                                rules={[{ required: true, message: "Байгууллага заавал" }]}
                            >
                                <Select
                                    showSearch
                                    loading={loadingInstitutions}
                                    optionFilterProp="label"
                                    options={institutions.map((item) => ({
                                        label: item.name,
                                        value: item.id,
                                    }))}
                                />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                name="currency"
                                label="Валют"
                                rules={[{ required: true, message: "Валют заавал" }]}
                            >
                                <Select options={currencyOptions} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item name="registrationDateTime" label="Бүртгэлийн огноо, цаг">
                                <DatePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm:ss" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item name="requestDateTime" label="Хүсэлтийн огноо, цаг">
                                <DatePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm:ss" />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={8}>
                            <Form.Item
                                name="paymentAmount"
                                label="Төлбөрийн дүн"
                                rules={[{ required: true, message: "Төлбөрийн дүн заавал" }]}
                            >
                                <InputNumber style={{ width: "100%" }} min={0} step={0.01} precision={2} />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item name="beneficiaryCompany" label="Хүлээн авагч байгууллага">
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                            <Form.Item name="beneficiaryBank" label="Хүлээн авагч банк">
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item name="beneficiaryAccount" label="Хүлээн авагчийн данс">
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col xs={24}>
                            <Form.Item name="paymentPurpose" label="Гүйлгээний утга">
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
}

export default ManualTransactionScheduleForm;